/**
 * 3CX Integration Component
 * Gemeinsame 3CX-Integrationskomponente für alle Business-Einstellungen
 */

import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Alert,
  Chip,
  CircularProgress,
  Divider,
  Collapse,
  IconButton,
} from "@mui/material";
import {
  Phone as PhoneIcon,
  Sync as SyncIcon,
  CompareArrows as CompareIcon,
  Update as UpdateIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { apiClient } from "../../services/apiClient";
import {
  ThreeCXStatus,
  ThreeCXComparison,
  ThreeCXSyncAnalysis,
} from "../../types/api-types";

interface ThreeCXIntegrationProps {
  title?: string;
  type: "opening-hours" | "holidays" | "phone-offsets";
  onSyncSuccess?: () => void;
  onSyncError?: (error: string) => void;
}

const ThreeCXIntegration: React.FC<ThreeCXIntegrationProps> = ({
  title = "3CX Telefonanlage Integration",
  type,
  onSyncSuccess,
  onSyncError,
}) => {
  const [threeCXStatus, setThreeCXStatus] = useState<ThreeCXStatus | null>(
    null
  );
  const [threeCXLoading, setThreeCXLoading] = useState(false);
  const [threeCXError, setThreeCXError] = useState<string | null>(null);
  const [syncAnalysis, setSyncAnalysis] = useState<ThreeCXSyncAnalysis | null>(
    null
  );
  const [comparison, setComparison] = useState<ThreeCXComparison | null>(null);
  const [hasCompared, setHasCompared] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);

  // Load 3CX status on mount
  useEffect(() => {
    load3CXStatus();
  }, []);

  const load3CXStatus = async () => {
    try {
      const status = await apiClient.get3CXStatus();
      setThreeCXStatus(status);
      setThreeCXError(null);
    } catch (err: any) {
      console.error("Fehler beim Laden des 3CX Status:", err);
      setThreeCXError("Verbindung zu 3CX konnte nicht hergestellt werden");
    }
  };

  const handleSync3CX = async () => {
    try {
      setThreeCXLoading(true);
      setThreeCXError(null);

      await apiClient.sync3CX();
      await load3CXStatus();
      onSyncSuccess?.();
    } catch (err: any) {
      const errorMessage = err.message || "Fehler bei der 3CX Synchronisation";
      setThreeCXError(errorMessage);
      onSyncError?.(errorMessage);
    } finally {
      setThreeCXLoading(false);
    }
  };

  const handleCompare3CX = async () => {
    try {
      setThreeCXLoading(true);
      setThreeCXError(null);

      let comparisonResult: ThreeCXComparison;
      switch (type) {
        case "opening-hours":
          comparisonResult = await apiClient.compare3CXOpeningHours();
          break;
        case "holidays":
          comparisonResult = await apiClient.compare3CXHolidays();
          break;
        case "phone-offsets":
          // Phone offsets comparison might not exist, use general comparison
          comparisonResult = await apiClient.compare3CXOpeningHours();
          break;
        default:
          throw new Error("Unbekannter Integrationstyp");
      }

      setComparison(comparisonResult);
      setHasCompared(true);

      const analysisResult = await apiClient.analyze3CXSync();
      setSyncAnalysis(analysisResult);
    } catch (err: any) {
      const errorMessage = err.message || "Fehler beim Vergleich mit 3CX";
      setThreeCXError(errorMessage);
      onSyncError?.(errorMessage);
    } finally {
      setThreeCXLoading(false);
    }
  };

  const handleForceUpdate3CX = async () => {
    try {
      setThreeCXLoading(true);
      setThreeCXError(null);

      const result = await apiClient.forceUpdate3CXAll();

      alert(
        `Force Update erfolgreich!\n${
          result.summary || "Daten wurden zu 3CX übertragen."
        }`
      );
      await load3CXStatus();
      await handleCompare3CX(); // Refresh comparison
      onSyncSuccess?.();
    } catch (err: any) {
      const errorMessage = err.message || "Fehler beim Force-Update von 3CX";
      setThreeCXError(errorMessage);
      onSyncError?.(errorMessage);
    } finally {
      setThreeCXLoading(false);
    }
  };

  // Helper functions
  const get3CXStatusColor = (): "success" | "warning" | "error" => {
    if (!threeCXStatus) return "error";
    if (!threeCXStatus.connected) return "error";
    if (!threeCXStatus.tokenStatus.isValid) return "warning";
    return "success";
  };

  const get3CXStatusText = (): string => {
    if (!threeCXStatus) return "Unbekannt";
    if (!threeCXStatus.connected) return "Getrennt";
    if (!threeCXStatus.tokenStatus.isValid) return "Token ungültig";
    return "Verbunden";
  };

  const get3CXStatusIcon = () => {
    const color = get3CXStatusColor();
    switch (color) {
      case "success":
        return <CheckCircleIcon color="success" />;
      case "warning":
        return <WarningIcon color="warning" />;
      case "error":
        return <ErrorIcon color="error" />;
      default:
        return <InfoIcon color="info" />;
    }
  };

  const isSyncNeeded = (): boolean => {
    if (!comparison) return false;
    return comparison.needsUpdate;
  };

  const canForceUpdate = (): boolean => {
    return hasCompared && isSyncNeeded() && threeCXStatus?.connected === true;
  };

  const isSyncDisabled = (): boolean => {
    if (!threeCXStatus?.connected) return true;
    // If we haven't compared yet or if no update is needed, sync is disabled
    return !hasCompared || !isSyncNeeded();
  };

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography
            variant="h6"
            fontWeight="medium"
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <PhoneIcon color="primary" />
            {title}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Chip
              icon={get3CXStatusIcon()}
              label={`3CX: ${get3CXStatusText()}`}
              color={get3CXStatusColor()}
              size="small"
              variant="outlined"
            />
            <IconButton
              onClick={() => setIsCollapsed(!isCollapsed)}
              size="small"
            >
              {isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
            </IconButton>
          </Box>
        </Box>

        <Collapse in={!isCollapsed}>
          <Divider sx={{ mb: 2 }} />

          {/* Status Information */}
          {threeCXStatus && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Verbindungsstatus:</strong>
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Chip
                  size="small"
                  label={`Verbunden: ${
                    threeCXStatus.connected ? "Ja" : "Nein"
                  }`}
                  color={threeCXStatus.connected ? "success" : "error"}
                  variant="outlined"
                />
                <Chip
                  size="small"
                  label={`Token: ${
                    threeCXStatus.tokenStatus.isValid ? "Gültig" : "Ungültig"
                  }`}
                  color={
                    threeCXStatus.tokenStatus.isValid ? "success" : "warning"
                  }
                  variant="outlined"
                />
                {threeCXStatus.lastSync && (
                  <Chip
                    size="small"
                    label={`Letzte Sync: ${new Date(
                      threeCXStatus.lastSync
                    ).toLocaleString("de-DE")}`}
                    color="info"
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>
          )}

          {/* Error Alert */}
          {threeCXError && (
            <Alert
              severity="error"
              sx={{ mb: 2, borderRadius: 1 }}
              onClose={() => setThreeCXError(null)}
            >
              {threeCXError}
            </Alert>
          )}

          {/* Sync Analysis */}
          {syncAnalysis && (
            <Alert
              severity={
                syncAnalysis.overallStatus === "NEEDS_UPDATE"
                  ? "warning"
                  : "success"
              }
              sx={{ mb: 2, borderRadius: 1 }}
            >
              <Typography variant="body2" fontWeight="medium">
                Synchronisationsanalyse:
              </Typography>
              <Typography variant="body2">
                Öffnungszeiten:{" "}
                {syncAnalysis.openingHours.needsUpdate
                  ? "Aktualisierung nötig"
                  : "Synchronisiert"}
              </Typography>
              <Typography variant="body2">
                Feiertage:{" "}
                {syncAnalysis.holidays.needsUpdate
                  ? "Aktualisierung nötig"
                  : "Synchronisiert"}
              </Typography>
            </Alert>
          )}

          {/* Comparison Results */}
          {comparison && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Vergleichsergebnis:</strong>
              </Typography>
              <Chip
                size="small"
                label={
                  comparison.needsUpdate
                    ? "Abweichungen gefunden"
                    : "Synchronisiert"
                }
                color={comparison.needsUpdate ? "warning" : "success"}
                variant="outlined"
              />
              {comparison.differences && comparison.differences.length > 0 && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {comparison.differences.length} Abweichung(en) gefunden
                </Typography>
              )}
            </Box>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              startIcon={
                threeCXLoading ? (
                  <CircularProgress size={16} />
                ) : (
                  <CompareIcon />
                )
              }
              onClick={handleCompare3CX}
              disabled={threeCXLoading || !threeCXStatus?.connected}
              size="small"
            >
              Vergleichen
            </Button>

            <Button
              variant="contained"
              startIcon={
                threeCXLoading ? <CircularProgress size={16} /> : <SyncIcon />
              }
              onClick={handleSync3CX}
              disabled={threeCXLoading || isSyncDisabled()}
              size="small"
              color={isSyncDisabled() ? "inherit" : "primary"}
            >
              {isSyncDisabled() ? "Synchronisiert" : "Synchronisieren"}
            </Button>

            <Button
              variant="contained"
              startIcon={
                threeCXLoading ? <CircularProgress size={16} /> : <UpdateIcon />
              }
              onClick={handleForceUpdate3CX}
              disabled={threeCXLoading || !canForceUpdate()}
              size="small"
              color="warning"
            >
              Force Update
            </Button>
          </Box>

          {/* Help Text */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              <InfoIcon
                sx={{ fontSize: 16, mr: 0.5, verticalAlign: "middle" }}
              />
              Die 3CX-Integration synchronisiert automatisch Änderungen.
              Verwenden Sie "Force Update" nur bei Abweichungen.
            </Typography>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default ThreeCXIntegration;
